// SPDX-License-Identifier: BUSL-1.1

/**
 * Confirmation dialog for feature flag kill switch actions.
 *
 */

import { useState } from 'react';
import { ShieldOff, ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';

interface KillSwitchDialogProps {
  flagKey: string;
  enabled: boolean;
  onConfirm: () => void;
  isPending: boolean;
}

export function KillSwitchDialog({
  flagKey,
  enabled,
  onConfirm,
  isPending,
}: KillSwitchDialogProps) {
  const [open, setOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger render={<Button variant="destructive" size="sm" className="gap-1.5" />}>
        {enabled ? <ShieldOff className="size-3.5" /> : <ShieldAlert className="size-3.5" />}
        {enabled ? 'Kill Switch' : 'Reactivate'}
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {enabled ? 'Activate Kill Switch' : 'Deactivate Kill Switch'}
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-neutral-400">
          {enabled
            ? `This will immediately disable the flag "${flagKey}" via kill switch. All consumers will see it as disabled.`
            : `This will reactivate the flag "${flagKey}" and remove the kill switch override.`}
        </p>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={isPending}
            onClick={() => {
              onConfirm();
              setOpen(false);
            }}
          >
            {isPending ? 'Processing...' : 'Confirm'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
